import type { PageHelp } from "../../help/types";

export const countryPageHelp: PageHelp = {
  title: "Country",
  intro:
    "The benefit catalog of one country. Pick a country to see its benefit categories, add categories from the global catalog on the Admin page, and adjust their consent, auto-enrollment and description for that country. The Analytics tab shows the Power BI report for the selected country.",
  sections: [
    "Getting around",
    "Tabs and toolbar",
    "First-time setup",
    "Reading the cards",
    "List view",
    "Category dialog",
  ],
  contexts: {
    "country-selected": 'Pick a country in the "Select Country" box first.',
    "setup-dialog":
      "Opens by itself when you pick a country that has no benefit categories yet.",
    "card-view":
      'Pick a country that has categories. Cards are the default view. If you see a table instead, open the settings menu in the header and set View Mode to "Grid View".',
    "list-view":
      'Pick a country that has categories, then open the settings menu in the header and set View Mode to "List View".',
    "add-dialog":
      'Pick a country, then click "Add Benefit Category" in the toolbar.',
    "category-dialog":
      'Click "Add Benefit Category" in the toolbar, or the Edit icon on a category card.',
    "edit-dialog":
      "Click the Edit icon on a category card, or the Edit icon in the Actions column of the list view.",
  },
  tips: [
    "A country category is a copy of a global category from the Admin page. It keeps the global name, but its consent, auto-enrollment and description can differ per country. Later changes on the Admin page do not update it.",
    "The categories you set up here are the ones that client companies in this country can get on the Company page.",
    "Each global category can be added to a country only once. If a category is missing from the Add Benefit Category list, the country already has it.",
    "View Mode (List View or Grid View) in the header settings menu is saved to your user settings and also applies to the other pages that list benefits.",
    "With a read-only role you can browse, search and open the analytics, but the add, edit and delete controls are hidden or greyed out.",
  ],
  steps: [
    // Getting around
    {
      selector: '[data-help="subheader-country-select"]',
      section: "Getting around",
      title: "Choose a country",
      body: "Pick the country whose catalog you want to manage. The rest of the page appears after you select a country. Global admins can pick any country, and Country admins only the countries assigned to them. When you change the country, the search is cleared.",
    },
    {
      selector: '[data-help="subheader-refresh"]',
      section: "Getting around",
      title: "Reload the categories",
      body: "Fetches the categories of the selected country again from the server, for example after a colleague has changed them.",
    },
    {
      selector: '[data-help="subheader-search"]',
      section: "Getting around",
      title: "Search categories by name",
      body: "Filters the cards or the table to categories whose name contains the text you type. Upper and lower case do not matter. The search is available only when the selected country has at least one category. Save Order is greyed out while a search is active.",
    },

    // Tabs and toolbar
    {
      selector: '[data-help="country-tab-benefits"]',
      section: "Tabs and toolbar",
      context: "country-selected",
      title: "Work with the country's categories",
      body: "Shows the benefit categories of the selected country, as cards or as a table depending on your View Mode setting.",
    },
    {
      selector: '[data-help="country-tab-analytics"]',
      section: "Tabs and toolbar",
      context: "country-selected",
      title: "Open the country's analytics",
      body: "Replaces the catalog with the embedded Power BI analytics report for the selected country. Move between report pages with the navigation inside the report.",
    },
    {
      selector: '[data-help="benefit-cards-save-order"]',
      section: "Tabs and toolbar",
      context: "country-selected",
      title: "Save your card order",
      body: "Drag the cards into the order you want, then click Save Order. The order is saved to your own user settings, separately for each country. The button is shown only in the card view and is greyed out while a search is active.",
    },
    {
      selector: '[data-help="country-add-category"]',
      section: "Tabs and toolbar",
      context: "country-selected",
      title: "Add a category to the country",
      body: 'Opens the "Add Benefit Category" dialog, where you pick a global category to add to this country. Greyed out for read-only roles, and when the country already has every global category.',
    },

    // First-time setup
    {
      selector: '[data-help="select-benefit-categories-table"]',
      section: "First-time setup",
      context: "setup-dialog",
      title: "Choose the categories for the country",
      body: 'Lists every global category. Click a row to tick or untick it in the Included column. The dialog title, "Add Benefits for:", shows the country you are setting up.',
    },
    {
      selector: '[data-help="select-benefit-categories-select-all"]',
      section: "First-time setup",
      context: "setup-dialog",
      title: "Tick all categories",
      body: "Marks every global category as included, so that all of them are added to the country when you save.",
    },
    {
      selector: '[data-help="select-benefit-categories-clear-all"]',
      section: "First-time setup",
      context: "setup-dialog",
      title: "Untick all categories",
      body: "Removes the tick from every category. Use it to start from an empty selection, then tick only the categories you need.",
    },
    {
      selector: '[data-help="select-benefit-categories-save"]',
      section: "First-time setup",
      context: "setup-dialog",
      title: "Add the ticked categories",
      body: "Creates a country copy of each ticked category and closes the dialog, and the categories appear on the page. If you close the dialog without saving, the country stays empty. You can still add categories one at a time with Add Benefit Category.",
    },

    // Reading the cards
    {
      selector: '[data-help="benefit-card"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      title: "Read a category card",
      body: "Each card shows the category's cover picture, name and description. Hover over the name or the description to read the full text. To change the order, drag a card to a new position, then click Save Order.",
    },
    {
      selector: '[data-help="benefit-card-edit"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "admin",
      title: "Edit a category",
      body: 'Opens the "Edit Category" dialog for this country\'s copy of the category. Your changes apply only to this country. The global category and other countries do not change.',
    },
    {
      selector: '[data-help="benefit-card-documents"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "admin",
      title: "Manage the category's documents",
      body: 'Opens "Manage Documents", where you upload files to this country\'s category and edit or delete its documents. Document changes are saved in that dialog, separately from the category itself.',
    },
    {
      selector: '[data-help="benefit-card-delete"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "superadmin",
      title: "Delete a category",
      body: "Asks for confirmation, then removes the category and its documents from this country. The delete also removes the client categories based on it and the client benefits built on those. The server accepts a category delete only from members of the Global admins group.",
    },

    // List view
    {
      selector: '[data-help="benefit-category-column-cover"]',
      section: "List view",
      context: "list-view",
      title: "Check the cover picture",
      body: "Shows a thumbnail of the cover picture. Hover over it for a larger preview. You cannot change cover pictures on this page.",
    },
    {
      selector: '[data-help="benefit-category-column-name"]',
      section: "List view",
      context: "list-view",
      title: "Read the category name",
      body: "The name comes from the global category on the Admin page. It is read-only on this page.",
    },
    {
      selector: '[data-help="benefit-category-column-documents"]',
      section: "List view",
      context: "list-view",
      title: "Open a category's documents",
      body: 'The button in each row shows how many documents the category has ("No data" when there are none). Click it to open "Manage Documents" for that category. Read-only roles see the button greyed out.',
    },
    {
      selector: '[data-help="benefit-category-column-consent"]',
      section: "List view",
      context: "list-view",
      title: "Set whether consent is needed",
      body: "Choose Yes or No for this country. New benefits created under this category start with this value. Consent Needed and Is Auto Enrollment cannot both be Yes: the table does not prevent it, but the server rejects such a row when you click Save.",
    },
    {
      selector: '[data-help="benefit-category-column-auto-enrollment"]',
      section: "List view",
      context: "list-view",
      title: "Set auto-enrollment",
      body: "Choose Yes or No for this country. New benefits created under this category start with this value. Set Consent Needed to No first, because the server rejects a row with both set to Yes.",
    },
    {
      selector: '[data-help="benefit-category-column-description"]',
      section: "List view",
      context: "list-view",
      title: "Edit the description in the table",
      body: "Type in the cell to change the description for this country. The row is marked as changed and is sent to the server only when you click Save in the Actions column. The description is required, so the server rejects an empty description.",
    },
    {
      selector: '[data-help="benefit-category-column-actions"]',
      section: "List view",
      context: "list-view",
      title: "Save, undo or delete a row",
      body: "Edit opens the category dialog, and the bin deletes the category after confirmation. After you change a cell, Save and Undo appear: Save sends that row to the server, and Undo restores the last saved values. Country admins also see the bin, but the server accepts a category delete only from Global admins.",
    },

    // Category dialog
    {
      selector: '[data-help="benefit-category-dialog-select"]',
      section: "Category dialog",
      context: "add-dialog",
      minRole: "admin",
      title: "Pick the global category to add",
      body: 'The "Category" list shows only global categories that this country does not have yet. When you pick one, the dialog copies the name, consent and auto-enrollment values, cover picture and description from the global category. You can then change the consent, auto-enrollment and description for this country.',
    },
    {
      selector: '[data-help="benefit-category-dialog-name"]',
      section: "Category dialog",
      context: "edit-dialog",
      minRole: "admin",
      title: "Check the category name",
      body: "The edit dialog shows the name as a text field. But a country category keeps the name of its global category, so the server rejects a save with a different name.",
    },
    {
      selector: '[data-help="benefit-category-dialog-consent"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Set whether consent is needed",
      body: "New benefits created under this category in this country start with this value. If you choose Yes, Is Auto Enrollment is set to No and greyed out, because the two cannot both be Yes.",
    },
    {
      selector: '[data-help="benefit-category-dialog-auto-enrollment"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Set auto-enrollment",
      body: "New benefits created under this category in this country start with this value. If you choose Yes, Consent Needed is set to No. This option is greyed out while Consent Needed is Yes.",
    },
    {
      selector: '[data-help="benefit-category-dialog-cover"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "See the cover picture",
      body: "Shows the cover picture read-only. When you add a category, it gets the picture of the global category. Only Superadmins can choose cover pictures, on the Admin page.",
    },
    {
      selector: '[data-help="benefit-category-dialog-description"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Describe the category for this country",
      body: "Required, 2 to 4000 characters. When you add a category, the description of the global category is filled in, and you can change it for this country.",
    },
    {
      selector: '[data-help="benefit-category-dialog-attachments"]',
      section: "Category dialog",
      context: "add-dialog",
      minRole: "admin",
      title: "Attach documents to the new category",
      body: 'Under "Document List", click Upload to pick one or more files for this country\'s category. The files are listed below, and the bin icon removes a file from the list. Nothing is uploaded until you click Save, which uploads the files together with the category.',
    },
    {
      selector: '[data-help="benefit-category-dialog-documents"]',
      section: "Category dialog",
      context: "edit-dialog",
      minRole: "admin",
      title: "Manage documents from the dialog",
      body: 'Opens "Manage Documents" for the category you are editing. Document changes are saved in that dialog, separately from the Save button of the category dialog. The button shows how many documents the category has.',
    },
    {
      selector: '[data-help="benefit-category-dialog-save"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Save the category",
      body: "Adds the category to the country, or saves your changes, and closes the dialog. The button is greyed out while a field has a validation error, for example when no category is picked or the description is empty.",
    },
  ],
};
